/**
 * Station Service — the single "station" brain (Jose 2026-08-09, Shop Account model).
 *
 * The app IS the shop; a barber's PIN identifies the operator + loads their permissions.
 * A "station" is NOT a login or a gate — it is just a per-device TAG for audit: each iPad
 * is labelled (e.g. Station 4 = Kelly's chair) so that every PIN login writes a
 * "who logged in, at which station, when" trail. Powers part-timer scheduling and
 * "I sat on the left on Thursday" lookups. PINs work on ANY device (portable).
 *
 * See [[project-slik-pos-shop-account-plan-2026-08-09]]. Now just the per-device station TAG
 * (localStorage) + the login audit (station_sessions → Security → Audit Log); the floor-plan
 * editor that defined a station list was removed 2026-09-27.
 */
import { collection, addDoc, getDocs, serverTimestamp, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getActiveShopId } from './tenantQuery';

const DEVICE_STATION_KEY = 'slikpos_device_station';

export interface DeviceStation {
  stationId: string;
  stationName: string;
}

export interface StationOption {
  id: string;
  name: string;
}

/** One row of the station login audit trail. */
export interface StationSession {
  id: string;
  stationId: string | null;
  stationName: string;
  barberId: string;
  barberName: string;
  loginAt: Date | null;
  source: string;
}

class StationService {
  private _cachedShopId: string | null = null;

  /** This physical device's assigned station tag (per-device localStorage). null = Unassigned. */
  getDeviceStation(): DeviceStation | null {
    try {
      const raw = localStorage.getItem(DEVICE_STATION_KEY);
      return raw ? (JSON.parse(raw) as DeviceStation) : null;
    } catch {
      return null;
    }
  }

  setDeviceStation(station: DeviceStation | null): void {
    try {
      if (station && station.stationId) {
        localStorage.setItem(DEVICE_STATION_KEY, JSON.stringify(station));
      } else {
        localStorage.removeItem(DEVICE_STATION_KEY);
      }
    } catch (err) {
      console.error('[Station] Failed to persist device station:', err);
    }
  }

  // getStations() removed (Jose 2026-09-27): the Settings floor-plan editor that consumed it is gone.
  // The login audit tags the device station from getDeviceStation() (localStorage), no shop list needed.

  private async resolveShopId(): Promise<string | null> {
    if (this._cachedShopId) return this._cachedShopId;
    // H11 FIX (Jose 2026-10-29 audit): prefer the AUTHENTICATED user's shop (set by AuthContext via
    // setActiveShopId) so a multi-shop deployment tags the audit trail with the barber's real shop —
    // NOT whichever doc happens to sort first in `shops`. Single-shop today → same result, but correct
    // for multi-tenant. Fall back to the first shop doc only when no active shop is known.
    const active = getActiveShopId();
    if (active) { this._cachedShopId = active; return active; }
    try {
      const snap = await getDocs(collection(db, 'shops'));
      if (!snap.empty) {
        // Do NOT cache the fallback — the active shop may resolve on a later call once auth settles.
        return snap.docs[0].id;
      }
    } catch (err) {
      console.error('[Station] Failed to resolve shopId:', err);
    }
    return null;
  }

  /**
   * Append a login to the station audit trail: WHO logged in, at WHICH station, WHEN.
   * Best-effort — NEVER throws into the login flow (a denied write must not block sign-in).
   */
  async logStationLogin(barber: { barberId: string; barberName: string }): Promise<void> {
    try {
      const device = this.getDeviceStation();
      const shopId = await this.resolveShopId();
      await addDoc(collection(db, 'station_sessions'), {
        shopId: shopId || null,
        stationId: device?.stationId || null,
        stationName: device?.stationName || 'Unassigned',
        barberId: barber.barberId || 'unknown',
        barberName: barber.barberName || 'Unknown',
        loginAt: serverTimestamp(),
        source: 'pin',
      });
    } catch (err) {
      console.error('[Station] Failed to log station login (non-blocking):', err);
    }
  }

  /** Read the most recent station logins (audit trail), newest first. Empty on error. */
  async getRecentSessions(max = 50): Promise<StationSession[]> {
    try {
      const q = query(collection(db, 'station_sessions'), orderBy('loginAt', 'desc'), limit(max));
      const snap = await getDocs(q);
      return snap.docs.map(d => {
        const data = d.data() as any;
        const ts = data.loginAt;
        const loginAt = ts && typeof ts.toDate === 'function' ? ts.toDate() : null;
        return {
          id: d.id,
          stationId: data.stationId ?? null,
          stationName: data.stationName || 'Unassigned',
          barberId: data.barberId || 'unknown',
          barberName: data.barberName || 'Unknown',
          loginAt,
          source: data.source || 'pin',
        };
      });
    } catch (err) {
      console.error('[Station] Failed to load recent sessions:', err);
      return [];
    }
  }
}

export const stationService = new StationService();
export default stationService;
